import { ReactElement, useEffect, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";
import { journalsQueries, TitlePageFillersDropdown } from "@/entities/journal";
import { useCoordinator } from "@/shared/lib/hooks";
import { UserBubble } from "@shared/ui";

type ValidationErrorList = {
  id: number;
  description: string | ReactElement;
};

type IUseValidateTitle = {
  errorList: ValidationErrorList[];
};

export const useValidateTitle = (): IUseValidateTitle => {
  const { titleId } = useCoordinator() as { titleId: string };
  const { projectId } = useParams();
  const { data } = useQuery(journalsQueries.getTitleJournal({ titleId }));

  const [errorList, setErrorList] = useState<ValidationErrorList[]>([]);

  useEffect(() => {
    if (!data) return;

    const errors: ValidationErrorList[] = [];
    const tj = data.mainJournal?.titleJournal;

    if (!tj?.mainJournalNumber) {
      errors.push({
        id: 1,
        description: "Номер журнала не может быть пустым",
      });
    }

    if (!tj?.projectCharacteristics) {
      errors.push({ id: 2, description: "Заполните Характеристику проекта" });
    }

    if (!tj?.constructionStartDate) {
      errors.push({
        id: 3,
        description: "Заполните Дату начала строительства",
      });
    }

    if (!tj?.mainJournalStartDate) {
      errors.push({ id: 4, description: "Заполните Дату начала журнала" });
    }

    const validateCompanyContacts = (
      company: any,
      label: string,
      errorId: number,
    ) => {
      const missing = [];
      if (!company?.contacts?.email && !company?.contacts?.phone) {
        missing.push("email");
        missing.push("phone");
      }

      if (missing.length) {
        errors.push({
          id: errorId,
          description: (
            <div>
              {`${label} - у организации не указана контактная информация`}
              <br />
              Обратитесь к <TitlePageFillersDropdown text="администратору" />{" "}
              {company ? `(${company?.fullName || "-"})` : ""}
            </div>
          ),
        });
      }
    };

    const tech = tj?.technicalCustomer;
    const hasTechConstructionControls = Boolean(
      tech?.constructionControls?.length,
    );

    const dev = tj?.developer;
    if (!dev?.company?.companyId) {
      errors.push({
        id: 5,
        description:
          "Заполните Сведения о застройщике или эксплуатирующей организации или региональном операторе",
      });
    } else {
      validateCompanyContacts(dev?.company, "Застройщик", 34);
    }
    if (!dev?.representatives?.length) {
      const type = dev?.developerType;
      let label = "организации";
      if (type === "developer") {
        label = "застройщика";
      } else if (type === "OPERATING") {
        label = "эксплуатирующей организации";
      } else if (type === "REGIONAL_OPERATOR") {
        label = "регионального оператора";
      }

      errors.push({
        id: 6,
        description: (
          <div>
            Укажите Уполномоченного представителя {label}
            <br />
            {/* ({dev.company?.fullName}) */}
          </div>
        ),
      });
    }

    if (
      dev?.company?.companyId &&
      tech?.company?.companyId &&
      !hasTechConstructionControls
    ) {
      errors.push({
        id: 24,
        description: (
          <div>
            Вам нужно заполнить блок Уполномоченный представитель застройщика по
            стройконтролю
          </div>
        ),
      });
    }

    if (tech?.company?.companyId) {
      const sroBuilding = tech.company?.sro?.find(
        (sro) => sro?.sroType === "BUILDING",
      );

      if (!tech.representatives?.length) {
        errors.push({
          id: 7,
          description: (
            <div>
              Укажите Уполномоченный представитель технического заказчика
              <br />({tech.company.fullName})
            </div>
          ),
        });
      }

      if (!sroBuilding) {
        errors.push({
          id: 8,
          description: (
            <div>
              Технический заказчик — у организации не указано СРО строительства.
              Обратитесь к <TitlePageFillersDropdown text="администратору" />
              <br />({tech.company.fullName})
            </div>
          ),
        });
      }

      validateCompanyContacts(tech.company, "Технический заказчик", 26);
    }

    if (tech?.company?.companyId && !hasTechConstructionControls) {
      errors.push({
        id: 27,
        description: (
          <div>
            Вам нужно заполнить блок Уполномоченный представитель технического заказчика по стройконтролю
            <br />({tech.company.fullName})
          </div>
        ),
      });
    }

    const builder = tj?.buildingContractor;
    if (!builder) {
      errors.push({
        id: 9,
        description: "Укажите Лицо, осуществляющее строительство",
      });
    } else {
      if (
        !builder.representatives?.length ||
        !builder.constructionControls?.length
      ) {
        errors.push({
          id: 10,
          description: (
            <div>
              Укажите Уполномоченного представителя лица, осуществляющего
              строительство и представителя по вопросам стройконтроля
              <br />({builder.company?.fullName})
            </div>
          ),
        });
      }

      validateCompanyContacts(
        builder.company,
        "Лицо, осуществляющее строительство",
        25,
      );
    }

    const pdc = tj?.projectDocumentationContractor;
    if (!pdc?.company?.companyId) {
      errors.push({
        id: 33,
        description: (
          <div>
            Укажите Лицо, осуществляющее подготовку проектной документации
          </div>
        ),
      });
    } else {
      validateCompanyContacts(
        pdc?.company,
        "Лицо, осуществляющее подготовку проектной документации",
        30,
      );
    }

    if (tj?.designerSupervisions?.length) {
      const noReps = tj.designerSupervisions
        .filter((ds) => !ds.representatives?.length)
        .map((ds) => ds.company?.fullName)
        .filter(Boolean);

      if (noReps.length) {
        errors.push({
          id: 18,
          description: (
            <div>
              Укажите уполномоченного представителя авторского надзора для
              следующих компаний:
              <br />
              {noReps.map((name, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${name}-${idx}`}>
                  <span>({name})</span>
                  <br />
                </div>
              ))}
            </div>
          ),
        });
      }

      const incompleteReps = tj.designerSupervisions
        .flatMap((ds) =>
          (ds.representatives || []).map((rep) => {
            const { user } = rep;
            const missing: string[] = [];

            if (
              !user?.specialistNumberEngineering &&
              !user?.specialistNumberBuilding
            ) {
              missing.push("НОСТРОЙ или НОПРИЗ");
            }

            if (missing.length) {
              const fullName = [
                user?.surname,
                user?.firstName,
                user?.middleName,
              ]
                .filter(Boolean)
                .join(" ");
              return {
                company: ds.company?.fullName || "-",
                fullName,
                email: user?.userEmail || "-",
                id: user?.userId || "",
                missingFields: missing,
              };
            }

            return null;
          }),
        )
        .filter(Boolean);

      if (incompleteReps.length) {
        errors.push({
          id: 16,
          description: (
            <div>
              Авторский надзор — не указан номер в национальном реестре
              специалистов для уполномоченного. Обратитесь к{" "}
              <TitlePageFillersDropdown text="администратору" /> :
              <br />
              {incompleteReps.map((rep) => (
                <UserBubble key={rep?.id || ""} userId={rep?.id || ""}>
                  ({rep?.company}, {rep?.fullName}, {rep?.email})
                  <br />
                </UserBubble>
              ))}
            </div>
          ),
        });
      }

      tj.designerSupervisions.forEach((ds) => {
        if (ds.company?.companyId) {
          validateCompanyContacts(ds.company, "Авторский надзор", 31);
        }
      });
    }

    if (tj?.otherDevelopers?.length) {
      const noReps = tj.otherDevelopers
        .filter((dev) => !dev.representatives?.length)
        .map((dev) => dev.company?.fullName)
        .filter(Boolean);

      if (noReps.length) {
        errors.push({
          id: 21,
          description: (
            <div>
              Укажите уполномоченного представителя для следующих организаций из
              раздела &quot;Другие лица, осуществляющие строительство&quot;:
              <br />
              {noReps.map((name, idx) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={`${name}-${idx}`}>
                  <span>({name})</span>
                  <br />
                </div>
              ))}
            </div>
          ),
        });
      }

      tj.otherDevelopers.forEach((dev) => {
        if (dev.company?.companyId) {
          validateCompanyContacts(
            dev.company,
            "Другие лица, осуществляющие строительство",
            32,
          );
        }
      });
    }

    setErrorList(errors);
  }, [data, projectId]);

  return { errorList };
};
